import React from 'react';
import {
  Wallet,
  CircleArrowDown,
  CircleArrowUp,
  UsersRound,
  Gavel,
  Check,
  SlidersHorizontal,
} from 'lucide-react';
import '../../../../../styles/components/cash-flow-card.scss';
import { OMR } from '../../../../common/icons';
import { NavLink } from 'react-router-dom';
import { fmt } from '../../../../../utils/common';
import i18n from '../../../../../i18n';

const CASH_IN_FILTERS = [
  { key: 'enrollment', label: i18n.t('auction_enrollment'), icon: UsersRound },
  { key: 'auction_payment', label: i18n.t('auction_payment'), icon: Gavel },
  { key: 'recharge', label: i18n.t('wallet_recharge'), icon: Wallet },
];

const CASH_OUT_FILTERS = [
  { key: 'auction_payment_out', label: i18n.t('auction_payment'), icon: Gavel },
  { key: 'withdrawal', label: i18n.t('wallet_withdrawal'), icon: Wallet },
];

class CashFlowCards extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      // all sources on by default, so the page looks the same as before until the user touches a pill
      activeCashIn: CASH_IN_FILTERS.map((f) => f.key),
      activeCashOut: CASH_OUT_FILTERS.map((f) => f.key),
    };
  }

  toggleCashInFilter = (key) => {
    this.setState((prevState) => {
      const isActive = prevState.activeCashIn.includes(key);
      return {
        activeCashIn: isActive
          ? prevState.activeCashIn.filter((k) => k !== key)
          : [...prevState.activeCashIn, key],
      };
    });
  };

  setCashInFilter = (keys) => this.setState({ activeCashIn: keys });

  getCashInTotal = () => {
    const { data } = this.props;
    const { activeCashIn } = this.state;
    const totalsByKey = {
      enrollment: data?.enrollment?.sub_total?.amount || 0,
      auction_payment: data?.auction_payment_in?.sub_total?.amount || 0,
      recharge: data?.recharge?.sub_total?.amount || 0,
    };
    return activeCashIn.reduce((sum, key) => sum + (totalsByKey[key] || 0), 0);
  };

  toggleCashOutFilter = (key) => {
    this.setState((prevState) => {
      const isActive = prevState.activeCashOut.includes(key);
      return {
        activeCashOut: isActive
          ? prevState.activeCashOut.filter((k) => k !== key)
          : [...prevState.activeCashOut, key],
      };
    });
  };

  setCashOutFilter = (keys) => this.setState({ activeCashOut: keys });

  getCashOutTotal = () => {
    const { data } = this.props;
    const { activeCashOut } = this.state;
    const totalsByKey = {
      auction_payment_out: data?.auction_payment_out?.sub_total?.amount || 0,
      withdrawal: data?.withdrawal?.sub_total?.amount || 0,
    };
    return activeCashOut.reduce((sum, key) => sum + (totalsByKey[key] || 0), 0);
  };
  render() {
    const { data, search } = this.props;
    const { activeCashIn, activeCashOut } = this.state;
    const cashInTotal = this.getCashInTotal();
    const cashOutTotal = this.getCashOutTotal();
    const allKeys = CASH_IN_FILTERS.map((f) => f.key);
    const allOutKeys = CASH_OUT_FILTERS.map((f) => f.key);
    return (
      <div className="dashboard-content">
        {/* LEFT SIDE - CASH FLOW IN */}
        <div className="flow-card-container cash-in-container">
          <div className="flow-card-header">
            <div className="header-left">
              {/* <div className="accent-bar in-bar"></div> */}
              <div className="cf-main-icon green">
                <CircleArrowDown size={20} strokeWidth={1.8} />
              </div>
              <h2 className="flow-title">{i18n.t('cash_flow_in')}</h2>
            </div>
            <div className="">
              <div className="final-amount">
                <strong>
                  <OMR size={10} color={'#1a6b73'} />
                  {/* key={cashInTotal} replays the pop-in animation whenever
                      the selection changes, so the new figure is noticeable */}
                  <span key={cashInTotal} className="cf-amount-value">
                    {fmt(cashInTotal)}
                  </span>
                </strong>
              </div>
            </div>
          </div>

          {/* Source filter — choose which cards feed the total above */}
          <div className="cf-filter-row">
            <div className="cf-filter-label">
              <SlidersHorizontal size={12} strokeWidth={2.4} />
              <span>{i18n.t('sources')}</span>
            </div>

            <div className="cf-filter-pills">
              {CASH_IN_FILTERS.map(({ key, label, icon: Icon }) => {
                const isActive = activeCashIn.includes(key);
                return (
                  <button
                    key={key}
                    type="button"
                    className={`cf-pill ${isActive ? 'is-active' : ''}`}
                    onClick={() => this.toggleCashInFilter(key)}
                    aria-pressed={isActive}
                  >
                    <span className="cf-pill-check">
                      {isActive && <Check size={10} strokeWidth={3} />}
                    </span>
                    <Icon size={13} strokeWidth={2} />
                    <span className="cf-pill-label">{label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {activeCashIn.length === 0 ? (
            <div className="cf-empty-state">
              <p>{i18n.t('no_source_selected')}</p>
              <button
                type="button"
                className="cf-empty-cta"
                onClick={() => this.setCashInFilter(allKeys)}
              >
                {i18n.t('show_all_sources')}
              </button>
            </div>
          ) : (
            <>
              {/* Card 1: Auction Enrollment */}
              {activeCashIn.includes('enrollment') && (
                <div className="flow-item cf-card-enter">
                  <div className="item-header">
                    <div className="item-header-content">
                      <div className="icon-wrapper in-icon">
                        <UsersRound size={20} strokeWidth={2} />
                      </div>
                      <div className="item-title-section">
                        <h3 className="item-title">{i18n.t('auction_enrollment')}</h3>
                        <span className="item-badge in-badge">
                          {data?.enrollment?.sub_total?.count || 0} {i18n.t('enrollments')}
                        </span>
                      </div>
                    </div>
                    <NavLink
                      to={{
                        pathname: '/cash-flow/auction-enrollment',
                        search: new URLSearchParams(
                          Object.fromEntries(
                            Object.entries({
                              startDate: search?.startDate,
                              endDate: search?.endDate,
                              bidder: search?.bidder,
                              client: search?.client,
                              group: search?.group?.length ? search.group.join(',') : null,
                            }).filter(([_, v]) => v !== null && v !== '' && v !== undefined)
                          )
                        ).toString(),
                      }}
                      className="details-btn-l2"
                    >
                      {i18n.t('view_details')}
                    </NavLink>
                  </div>

                  <div className="item-content">
                    <div className="amount-line">
                      <span className="label">{i18n.t('online_enrollment_received')}</span>
                      <span className="value">
                        <OMR size={8} />
                        <span className="amount-text">
                          {fmt(data?.enrollment?.online?.amount || 0)}
                        </span>
                      </span>
                    </div>
                    <div className="amount-line">
                      <span className="label">{i18n.t('offline_enrollment_received')}</span>
                      <span className="value">
                        <OMR size={8} />
                        <span className="amount-text">
                          {fmt(data?.enrollment?.offline?.amount || 0)}
                        </span>
                      </span>
                    </div>
                    <div className="amount-line subtotal">
                      <span className="label subtotal-label">{i18n.t('subtotal_received')}</span>
                      <span className="value subtotal-value">
                        <OMR size={8} />
                        <span className="amount-text">
                          {fmt(data?.enrollment?.sub_total?.amount || 0)}
                        </span>
                      </span>
                    </div>
                    <div className="amount-line balance">
                      <span className="label">{i18n.t('wallet_enrollment')}</span>
                      <span className="value balance-value">
                        <OMR size={8} />
                        <span className="amount-text">
                          {fmt(data?.enrollment?.wallet?.amount || 0)}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Card 2: Auction Payment */}
              {activeCashIn.includes('auction_payment') && (
                <div className="flow-item cf-card-enter">
                  <div className="item-header">
                    <div className="item-header-content">
                      <div className="icon-wrapper in-icon">
                        <Gavel size={20} strokeWidth={2} />
                      </div>
                      <div className="item-title-section">
                        <h3 className="item-title">{i18n.t('auction_payment')}</h3>
                        <span className="item-badge in-badge">
                          {data?.auction_payment_in?.sub_total?.count || 0} {i18n.t('auctions')}
                        </span>
                      </div>
                    </div>
                    <NavLink
                      to={{
                        pathname: '/cash-flow/auction-payment',
                        search: new URLSearchParams(
                          Object.fromEntries(
                            Object.entries({
                              startDate: search?.startDate,
                              endDate: search?.endDate,
                              bidder: search?.bidder,
                              client: search?.client,
                              group: search?.group?.length ? search.group.join(',') : null,
                            }).filter(([_, v]) => v !== null && v !== '' && v !== undefined)
                          )
                        ).toString(),
                      }}
                      className="details-btn-l2"
                    >
                      {i18n.t('view_details')}
                    </NavLink>
                  </div>

                  <div className="item-content">
                    <div className="amount-line">
                      <span className="label">{i18n.t('client_amount_received_bid_vat')}</span>
                      <span className="value">
                        <OMR size={8} />
                        <span className="amount-text">
                          {fmt(data?.auction_payment_in?.client_amount?.amount || 0)}
                        </span>
                      </span>
                    </div>
                    <div className="amount-line">
                      <span className="label">{i18n.t('mzadcom_vat_received')}</span>
                      <span className="value">
                        <OMR size={8} />
                        <span className="amount-text">
                          {fmt(data?.auction_payment_in?.service_charge_vat?.amount || 0)}
                        </span>
                      </span>
                    </div>
                    <div className="amount-line">
                      <span className="label">{i18n.t('mzadcom_vat_received')}</span>
                      <span className="value">
                        <OMR size={8} />
                        <span className="amount-text">
                          {fmt(data?.auction_payment_in?.service_charge?.amount || 0)}
                        </span>
                      </span>
                    </div>
                    <div className="amount-line subtotal">
                      <span className="label subtotal-label">{i18n.t('subtotal_received')}</span>
                      <span className="value subtotal-value">
                        <OMR size={8} />
                        <span className="amount-text">
                          {fmt(data?.auction_payment_in?.sub_total?.amount || 0)}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Card 3: Wallet Recharge */}
              {activeCashIn.includes('recharge') && (
                <div className="flow-item cf-card-enter">
                  <div className="item-header">
                    <div className="item-header-content">
                      <div className="icon-wrapper in-icon">
                        <Wallet size={20} strokeWidth={2} />
                      </div>
                      <div className="item-title-section">
                        <h3 className="item-title">{i18n.t('wallet_recharge')}</h3>
                        <span className="item-badge in-badge">
                          {data?.recharge?.sub_total?.count || 0} {i18n.t('transactions')}
                        </span>
                      </div>
                    </div>
                    <NavLink
                      to={{
                        pathname: '/cash-flow/wallet/recharge',
                        search: new URLSearchParams(
                          Object.fromEntries(
                            Object.entries({
                              startDate: search?.startDate,
                              endDate: search?.endDate,
                              bidder: search?.bidder,
                            }).filter(([_, v]) => v !== null && v !== '' && v !== undefined)
                          )
                        ).toString(),
                      }}
                      className="details-btn-l2"
                    >
                      {i18n.t('view_details')}
                    </NavLink>
                  </div>

                  <div className="item-content">
                    <div className="amount-line">
                      <span className="label">{i18n.t('online_transfer_received')}</span>
                      <span className="value">
                        <OMR size={8} />
                        <span className="amount-text">
                          {fmt(data?.recharge?.online?.amount || 0)}
                        </span>
                      </span>
                    </div>
                    <div className="amount-line">
                      <span className="label">{i18n.t('offline_transfer_received')}</span>
                      <span className="value">
                        <OMR size={8} />
                        <span className="amount-text">
                          {fmt(data?.recharge?.offline?.amount || 0)}
                        </span>
                      </span>
                    </div>
                    <div className="amount-line subtotal">
                      <span className="label subtotal-label">{i18n.t('subtotal_received')}</span>
                      <span className="value subtotal-value">
                        <OMR size={8} />
                        <span className="amount-text">
                          {fmt(data?.recharge?.sub_total?.amount || 0)}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* RIGHT SIDE - CASH FLOW OUT */}
        <div className="flow-card-container cash-out-container">
          <div className="flow-card-header">
            <div className="header-left">
              <div className="cf-main-icon red">
                <CircleArrowUp size={20} strokeWidth={1.8} />
              </div>
              <h2 className="flow-title">{i18n.t('cash_flow_out')}</h2>
            </div>
            <div className="">
              <div className="final-amount">
                <strong>
                  <OMR size={10} color={'#1a6b73'} />
                  <span key={cashOutTotal} className="cf-amount-value">
                    {fmt(cashOutTotal)}
                  </span>
                </strong>
              </div>
            </div>
          </div>

          {/* Source filter — choose which cards feed the total above */}
          <div className="cf-filter-row">
            <div className="cf-filter-label">
              <SlidersHorizontal size={12} strokeWidth={2.4} />
              <span>{i18n.t('sources')}</span>
            </div>

            <div className="cf-filter-pills">
              {CASH_OUT_FILTERS.map(({ key, label, icon: Icon }) => {
                const isActive = activeCashOut.includes(key);
                return (
                  <button
                    key={key}
                    type="button"
                    className={`cf-pill ${isActive ? 'is-active' : ''}`}
                    onClick={() => this.toggleCashOutFilter(key)}
                    aria-pressed={isActive}
                  >
                    <span className="cf-pill-check">
                      {isActive && <Check size={10} strokeWidth={3} />}
                    </span>
                    <Icon size={13} strokeWidth={2} />
                    <span className="cf-pill-label">{label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {activeCashOut.length === 0 && (
            <div className="cf-empty-state">
              <p>{i18n.t('no_source_selected')}</p>
              <button
                type="button"
                className="cf-empty-cta"
                onClick={() => this.setCashOutFilter(allOutKeys)}
              >
                {i18n.t('show_all_sources')}
              </button>
            </div>
          )}

          {/* Card 1: Auction Payment Out */}
          {activeCashOut.includes('auction_payment_out') && (
            <div className="flow-item cf-card-enter">
              <div className="item-header">
                <div className="item-header-content">
                  <div className="icon-wrapper out-icon">
                    <Gavel size={20} strokeWidth={2} />
                  </div>
                  <div className="item-title-section">
                    <h3 className="item-title">{i18n.t('auction_payment')}</h3>
                    <span className="item-badge out-badge">
                      {data?.auction_payment_out?.sub_total?.count || 0} {i18n.t('auctions')}
                    </span>
                  </div>
                </div>
                <NavLink
                  to={{
                    pathname: '/cash-flow/auction-payment',
                    search: new URLSearchParams(
                      Object.fromEntries(
                        Object.entries({
                          startDate: search?.startDate,
                          endDate: search?.endDate,
                          bidder: search?.bidder,
                          client: search?.client,
                          group: search?.group?.length ? search.group.join(',') : null,
                        }).filter(([_, v]) => v !== null && v !== '' && v !== undefined)
                      )
                    ).toString(),
                  }}
                  className="details-btn-l2"
                >
                  {i18n.t('view_details')}
                </NavLink>
              </div>

              <div className="item-content">
                <div className="amount-line">
                  <span className="label">{i18n.t('client_amount_bid_vat')}</span>
                  <span className="value">
                    <OMR size={8} />
                    <span className="amount-text">
                      {fmt(data?.auction_payment_out?.client_amount?.amount || 0)}
                    </span>
                  </span>
                </div>

                <div className="amount-line">
                  <span className="label">{i18n.t('service_charge_vat_to_vat_account')}</span>
                  <span className="value">
                    <OMR size={8} />
                    <span className="amount-text">
                      {fmt(data?.auction_payment_out?.service_charge_vat?.amount || 0)}
                    </span>
                  </span>
                </div>

                <div className="amount-line">
                  <span className="label">|{i18n.t('mzadcom_profit_service_charge')}</span>
                  <span className="value">
                    <OMR size={8} />
                    <span className="amount-text">
                      {fmt(data?.auction_payment_out?.service_charge?.amount || 0)}
                    </span>
                  </span>
                </div>
                <div className="amount-line subtotal">
                  <span className="label subtotal-label">{i18n.t('subtotal_sent')}</span>
                  <span className="value subtotal-value">
                    <OMR size={8} />
                    <span className="amount-text">
                      {fmt(data?.auction_payment_out?.sub_total?.amount || 0)}
                    </span>
                  </span>
                </div>
                {/* <div className="amount-line balance">
                  <span className="label">Balance to Send</span>
                  <span className="value balance-value">
                    <OMR size={8} />
                    {data?.auction_payment_out?.sub_total?.amount || 0}
                  </span>
                </div> */}
              </div>
            </div>
          )}

          {/* Card 2: Wallet Withdrawal */}
          {activeCashOut.includes('withdrawal') && (
            <div className="flow-item cf-card-enter">
              <div className="item-header">
                <div className="item-header-content">
                  <div className="icon-wrapper out-icon">
                    <Wallet size={20} strokeWidth={2} />
                  </div>
                  <div className="item-title-section">
                    <h3 className="item-title">{i18n.t('wallet_withdrawal')}</h3>
                    <span className="item-badge out-badge">
                      {data?.withdrawal?.sub_total?.count || 0} {i18n.t('bank_transfers')}
                    </span>
                  </div>
                </div>
                <NavLink
                  to={{
                    pathname: '/cash-flow/wallet/withdrawal',
                    search: new URLSearchParams(
                      Object.fromEntries(
                        Object.entries({
                          startDate: search?.startDate,
                          endDate: search?.endDate,
                          bidder: search?.bidder,
                          client: search?.client,
                          group: search?.group?.length ? search.group.join(',') : null,
                        }).filter(([_, v]) => v !== null && v !== '' && v !== undefined)
                      )
                    ).toString(),
                  }}
                  className="details-btn-l2"
                >
                  {i18n.t('view_details')}
                </NavLink>
              </div>

              <div className="item-content">
                <div className="amount-line">
                  <span className="label">{i18n.t('withdrawal_completed')}</span>
                  <span className="value">
                    <OMR size={8} />
                    <span className="amount-text">
                      {fmt(data?.withdrawal?.wallet_withdrawal?.amount || 0)}
                    </span>
                  </span>
                </div>
                <div className="amount-line">
                  <span className="label">{i18n.t('auction_payment_using_wallet')}</span>
                  <span className="value">
                    <OMR size={8} />
                    <span className="amount-text">
                      {fmt(data?.withdrawal?.auction_payment?.amount || 0)}
                    </span>
                  </span>
                </div>
                <div className="amount-line subtotal">
                  <span className="label subtotal-label">{i18n.t('subtotal_sent')}</span>
                  <span className="value subtotal-value">
                    <OMR size={8} />
                    <span className="amount-text">
                      {fmt(data?.withdrawal?.sub_total?.amount || 0)}
                    </span>
                  </span>
                </div>
                <div className="amount-line balance">
                  <span className="label">{i18n.t('withdrawal_pending')}</span>
                  <span className="value balance-value">
                    <OMR size={8} />
                    <span className="amount-text">
                      {fmt(data?.withdrawal?.wallet_pending?.amount || 0)}
                    </span>
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default CashFlowCards;
